import React, { createContext, useEffect, useState, useContext } from "react";
import axios from "../helper/axios";
import { AuthContext } from "./AuthContext";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const loggedInUser = localStorage.getItem("token");
  const loggedInUserId = localStorage.getItem("userId");

  const { token } = useContext(AuthContext);

  const [totalAmount, setTotalAmount] = useState(0);

  const [temporaryCartItems, setTemporaryCartItems] = useState(
    JSON.parse(localStorage.getItem("temporaryCartItems")) || []
  );

  //Function to calculate total amount
  const calculateTotalAmount = (items) => {
    return items.reduce((total, item) => total + item.price * item.qty, 0);
  };
  // Function to add a product to temporary cart
  const addToTemporaryCart = (product) => {
    // Check if the item is already in the temporary cart
    const existingItem = temporaryCartItems.find(
      (tempItem) => tempItem.product_id === product.product_id
    );

    if (existingItem) {
      // Increment the quantity of the existing item
      const updatedItems = temporaryCartItems.map((tempItem) =>
        tempItem.product_id === product.product_id
          ? { ...tempItem, qty: tempItem.qty + 1 }
          : tempItem
      );
      setTemporaryCartItems(updatedItems);
    } else {
      // Add the item to temporary cart with quantity 1
      setTemporaryCartItems([...temporaryCartItems, { ...product, qty: 1 }]);
    }

    // Update localStorage
    localStorage.setItem(
      "temporaryCartItems",
      JSON.stringify([...temporaryCartItems, { ...product, qty: 1 }])
    );
  };

  // Function to merge temporaryCartItems into cartItems on user login
  const mergeTemporaryCart = async () => {
    if (token) {
      // User is logged in, and there are products in temporaryCartItems
      if (temporaryCartItems.length > 0) {
        try {
          // Fetch the user's existing cart items from the server
          const response = await axios.get(
            `cart_read?user_id=${loggedInUserId}`
          );
          const existingCartItems = response.data.data;

          // Check if the cart is empty on the server
          if (existingCartItems.length === 0) {
            // If the cart is empty, add items from temporaryCartItems
            for (const tempItem of temporaryCartItems) {
              const addItemToCart = {
                user_id_fk: loggedInUserId,
                product_id_fk: tempItem.product_id,
                qty: tempItem.qty,
                is_in_cart: true,
              };

              // Make an API request to add the item to the cart
              await axios.post("cart_insert", addItemToCart);
            }
          } else {
            // If the cart is not empty, update items from temporaryCartItems
            for (const tempItem of temporaryCartItems) {
              const existingCartItem = existingCartItems.find(
                (item) => item.product_id === tempItem.product_id
              );

              if (existingCartItem) {
                // Product exists in the cart, update its quantity
                const updateItemInCart = {
                  cart_id: existingCartItem.cart_id,
                  product_id_fk: tempItem.product_id,
                  qty: existingCartItem.qty + tempItem.qty, // Update the quantity
                };

                // Make an API request to update the item in the cart
                await axios.put("update_item_in_cart", updateItemInCart);
              } else {
                // Product doesn't exist in the cart, add it
                const addItemToCart = {
                  user_id_fk: loggedInUserId,
                  product_id_fk: tempItem.product_id,
                  qty: tempItem.qty,
                  is_in_cart: true,
                };

                // Make an API request to add the item to the cart
                await axios.post("cart_insert", addItemToCart);
              }
            }
          }

          // Clear temporaryCartItems after merging
          clearTemporaryCart();
        } catch (error) {
          console.error(
            "Error adding/updating products in the backend:",
            error
          );
          // Handle the error as needed
        }
      }
    }
  };

  // Call mergeTemporaryCart when the component mounts to check for products to merge
  useEffect(() => {
    mergeTemporaryCart();
  }, [token]);

  const removeFromTemporaryCart = (itemId) => {
    // Check if the item is in the temporary cart
    const existingItem = temporaryCartItems.find(
      (tempItem) => tempItem.product_id === itemId
    );

    if (existingItem) {
      if (existingItem.qty > 1) {
        // Decrement the quantity of the existing item
        const updatedItems = temporaryCartItems.map((tempItem) =>
          tempItem.product_id === itemId
            ? { ...tempItem, qty: tempItem.qty - 1 }
            : tempItem
        );
        setTemporaryCartItems(updatedItems);
      } else {
        if (
          window.confirm(
            "Are you sure you want to remove the Product From Temporary Cart ?"
          )
        ) {
          // Remove the item from temporary cart if quantity is 1
          const updatedItems = temporaryCartItems.filter(
            (tempItem) => tempItem.product_id !== itemId
          );
          setTemporaryCartItems(updatedItems);
        }
      }

      // Update localStorage
      localStorage.setItem(
        "temporaryCartItems",
        JSON.stringify(temporaryCartItems)
      );
    }
  };

  // Function to clear temporary cart
  const clearTemporaryCart = () => {
    setTemporaryCartItems([]);
    localStorage.removeItem("temporaryCartItems");
  };

  const fetchCartItems = async () => {
    if (loggedInUser) {
      try {
        const response = await axios.get(`cart_read?user_id=${loggedInUserId}`);
        const data = response.data.data;
        console.log(data, "data");
        setCartItems(data);
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          setCartItems([]); // Set cart items to an empty array if 404 error (not found)
        }
      }
    }
  };

  useEffect(() => {
    if (loggedInUser) {
      fetchCartItems();
    } else {
      // Set temporary cart items for non-logged-in users
      // console.log("temporaryCartItems", temporaryCartItems);
      setCartItems(temporaryCartItems);
      // setTotalAmount(calculateTotalAmount(temporaryCartItems)); // Calculate total amount
    }
  }, [loggedInUser, temporaryCartItems]);

  useEffect(() => {
    // Calculate total amount whenever the cart items change
    const total = loggedInUser
      ? calculateTotalAmount(cartItems)
      : calculateTotalAmount(temporaryCartItems);

    setTotalAmount(total);
  }, [cartItems, temporaryCartItems]);

  const addToCart = async (product, userId) => {
    if (loggedInUser) {
      // User is logged in, add product to the backend
      const addItemToCart = {
        user_id_fk: userId,
        product_id_fk: product.product_id,
        is_in_cart: true,
      };

      // Check if the product is already in the cartItems fetched from the backend
      const existingCartItem = cartItems.find(
        (item) =>
          item.product_id === product.product_id &&
          item.cart_id === product.cart_id
      );

      if (existingCartItem) {
        // console.log(existingCartItem, "existingCartItem");
        // Product is already in the cart, call the update_item_in_cart API
        const updateItemInCart = {
          cart_id: existingCartItem.cart_id,
          product_id_fk: product.product_id,
          // user_id_fk: userId,
          // is_in_cart: true,
        };
        try {
          await axios.put("/update_item_in_cart", updateItemInCart);
          fetchCartItems();
          alert("Product Updated in Cart Successfully");
        } catch (error) {
          console.error(error);
        }
      } else {
        try {
          await axios.post("/cart_insert", addItemToCart);
          fetchCartItems();
          alert("Product Added to Cart Successfully");
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      //User is not logged in, add product to temporary cart
      addToTemporaryCart(product);
      setCartItems((prevItems) => [...prevItems, { ...product }]);
      alert("Product Added to Temporary Cart");
    }
  };

  const clearCart = async () => {
    if (window.confirm("Are you sure you want to clear the cart?")) {
      try {
        const token = localStorage.getItem("token");

        if (token) {
          // User is logged in, delete products from backend
          await Promise.all(
            cartItems.map(async (product) => {
              try {
                await axios.delete(`/cart_delete/${product.cart_id}`, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                });
              } catch (error) {
                console.error(error.response.data);
                throw new Error("Failed to delete product.");
              }
              fetchCartItems();
            })
          );
        } else {
          // User is not logged in, clear temporary cart
          clearTemporaryCart();
          localStorage.removeItem("cartItems");
          setCartItems([]);
        }
        alert("Cart Cleared Successfully!");
      } catch (error) {
        console.error(error.message);
        alert("Failed to clear the cart.");
      }
    }
  };

  const removeProduct = (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      const updatedTemporaryCartItems = temporaryCartItems.filter(
        (item) => item.product_id !== productId
      );
      localStorage.setItem(
        "temporaryCartItems",
        JSON.stringify(updatedTemporaryCartItems)
      );
      setTemporaryCartItems(updatedTemporaryCartItems);
    }
  };

  const removeFromCartList = async (productSku) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      const token = localStorage.getItem("token");

      if (token) {
        // User is logged in, remove product from the backend
        try {
          await axios.delete(`/cart_delete/${productSku}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          alert("Selected Products Deleted Successfully!");
          fetchCartItems();
        } catch (error) {
          console.error(error.response.data);
          alert("Failed to delete products.");
        }
      } else {
        const updatedCartItems = cartItems.filter(
          (item) => item.sku !== productSku
        );
        localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
        removeFromTemporaryCart(productSku); // Remove from temporary cart
        setCartItems(updatedCartItems);
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        clearCart,
        removeFromCartList,
        removeProduct,
        temporaryCartItems,
        setTemporaryCartItems,
        addToTemporaryCart,
        removeFromTemporaryCart,
        fetchCartItems,
        clearTemporaryCart,
        totalAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
